import type { AnyAction } from "redux";
import * as actionTypes from "./actionTypes.ts";
import * as dashboardTypes from "../actions/types.js";
import type { FeedFilterOption, FeedPost, FeedPortfolio } from "../../types/feed.ts";

export type FeedViewState = {
  selectedOption: FeedFilterOption;
  order: string | null;
  type: string | null;
};

export type FeedItemOrderEntry = { type: "post" | "portfolio"; id: number };

export type FeedState = {
  feedView: FeedViewState;
  posteos: FeedPost[];
  ownPosteos: FeedPost[];
  allPortfolios: FeedPortfolio[];
  userPortfolios: FeedPortfolio[];
  recentPost: FeedPost | null;
  itemOrder: FeedItemOrderEntry[];
};

const INITIAL_STATE: FeedState = {
  feedView: {
    selectedOption: "Mostrar",
    order: null,
    type: null,
  },
  posteos: [],
  ownPosteos: [],
  allPortfolios: [],
  userPortfolios: [],
  recentPost: null,
  itemOrder: [],
};

const updatePosteoFromList = (
  state: FeedState,
  posteo: Partial<FeedPost> & { id: number }
): FeedState => {
  const updatedPosteos = (state.posteos || []).map((p) =>
    p.id === posteo.id ? { ...p, ...posteo } : p
  );
  const updatedOwnPosteos = (state.ownPosteos || []).map((p) =>
    p.id === posteo.id ? { ...p, ...posteo } : p
  );
  return { ...state, posteos: updatedPosteos, ownPosteos: updatedOwnPosteos };
};

const mergeLikesForPost = (
  state: FeedState,
  postId: number,
  mutate: (likes: FeedPost["likes"]) => FeedPost["likes"]
): FeedState => {
  const postIdStr = String(postId);
  const apply = (p: FeedPost) =>
    String(p.id) === postIdStr ? { ...p, likes: mutate(p.likes ?? []) ?? [] } : p;
  return {
    ...state,
    posteos: (state.posteos || []).map(apply),
    ownPosteos: (state.ownPosteos || []).map(apply),
  };
};

export default function feedReducer(
  state: FeedState = INITIAL_STATE,
  action: AnyAction
): FeedState {
  switch (action.type) {
    case actionTypes.SET_FEED_VIEW: {
      const next = {
        ...state,
        feedView: {
          selectedOption: action.payload?.selectedOption ?? state.feedView.selectedOption,
          order: action.payload?.order ?? state.feedView.order,
          type: action.payload?.type ?? state.feedView.type,
        },
      };
      if (Array.isArray(action.payload?.itemOrder)) {
        next.itemOrder = action.payload.itemOrder;
      }
      return next;
    }
    case actionTypes.SET_FEED_ITEM_ORDER:
      return {
        ...state,
        itemOrder: Array.isArray(action.payload) ? action.payload : [],
      };
    case actionTypes.FEED_SHUFFLE_ORDER: {
      const list = [...(state.itemOrder || [])];
      for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
      }
      return { ...state, itemOrder: list };
    }
    case dashboardTypes.GET_ALL_PORTFOLIOS:
      return { ...state, allPortfolios: action.payload ?? [] };
    case dashboardTypes.GET_USER_PORTFOLIOS:
      return { ...state, userPortfolios: action.payload ?? [] };
    case dashboardTypes.GET_POSTEOS:
      return { ...state, posteos: action.payload ?? [] };
    case dashboardTypes.GET_OWN_POSTEOS:
      return { ...state, ownPosteos: action.payload ?? [] };
    case dashboardTypes.RECENT_POST:
      return { ...state, recentPost: action.payload ?? null };
    case dashboardTypes.UPDATE_POSTEO_INFO:
      return updatePosteoFromList(state, action.payload?.posteo ?? action.payload);
    case dashboardTypes.GET_POSTEO_WITH_COMMENTS: {
      const { post, comments } = action.payload ?? {};
      if (!post) return state;
      const normComments = Array.isArray(comments) ? comments : [];
      return {
        ...state,
        posteos: (state.posteos || []).map((p) =>
          p.id === post.id ? { ...post, comments: normComments } : p
        ),
        ownPosteos: (state.ownPosteos || []).map((p) =>
          p.id === post.id ? { ...post, comments: normComments } : p
        ),
      };
    }
    case actionTypes.FEED_OPTIMISTIC_LIKE_POST: {
      const { postId, userId, add } = action.payload ?? {};
      if (postId == null || userId == null) return state;
      return mergeLikesForPost(state, Number(postId), (likes) => {
        const list = Array.isArray(likes) ? [...likes] : [];
        const idx = list.findIndex((l) => String(l?.user?.id) === String(userId));
        if (add) {
          if (idx >= 0) return list;
          return [...list, { user: { id: userId } }];
        }
        if (idx < 0) return list;
        return list.filter((_, i) => i !== idx);
      });
    }
    case actionTypes.FEED_MERGE_POST_PARTIAL: {
      const { postId, likes } = action.payload ?? {};
      if (postId == null) return state;
      const id = Number(postId);
      if (likes !== undefined) {
        return mergeLikesForPost(state, id, () =>
          Array.isArray(likes) ? likes : []
        );
      }
      return state;
    }
    case actionTypes.FEED_PREPEND_POST: {
      const post = action.payload;
      if (!post || post.id == null) return state;
      const rawImages = Array.isArray(post.images) ? post.images : [];
      const images = rawImages.map((img) =>
        typeof img === "string" ? { image: img } : img
      );
      const normalized = {
        ...post,
        comments: Array.isArray(post.comments) ? post.comments : [],
        likes: Array.isArray(post.likes) ? post.likes : [],
        images,
        video: Array.isArray(post.video) ? post.video : [],
      } as FeedPost;
      const entry: FeedItemOrderEntry = { type: "post", id: Number(post.id) };
      return {
        ...state,
        posteos: [normalized, ...(state.posteos || [])],
        ownPosteos: [normalized, ...(state.ownPosteos || [])],
        itemOrder: [entry, ...(state.itemOrder || [])],
      };
    }
    case actionTypes.FEED_MERGE_POSTS_COMMENTS: {
      const commentsByPostId = action.payload?.commentsByPostId as
        | Record<number, FeedPost["comments"]>
        | undefined;
      if (!commentsByPostId || typeof commentsByPostId !== "object") return state;
      const applyComments = (p: FeedPost): FeedPost =>
        commentsByPostId[p.id] !== undefined
          ? { ...p, comments: commentsByPostId[p.id] }
          : p;
      return {
        ...state,
        posteos: (state.posteos || []).map(applyComments),
        ownPosteos: (state.ownPosteos || []).map(applyComments),
      };
    }
    default:
      return state;
  }
}
